import { db } from '@app/icare/client/db'
import type { ResultTypeFromQueryResult } from '@app/icare/client/db/client/localQueryOptions'
import { localPaginatedQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import ArticleStatusBadge from '@app/icare/components/status/ArticleStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import { ArticleStatusEnum } from '@app/icare/entities/article/schema/Article'
import { entityRegistry } from '@app/icare/entities/registry'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { BooleanStatusBadge } from '@pkg/ui/badge'
import { AddEntityButtonLink, ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainerRuntime'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Divider, Space, Table, Typography } from 'antd'
import { sql } from 'kysely'
import React from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/artikel/')({
  component: RouteComponent,
})

const Filter = QueryBaseSchema.extend({
  sortBy: z
    .enum([
      'article.name',
      'article.manufacturer.name',
      'article.packaging.numberOfUnits',
      'article.packaging.amount',
      'article.packaging.unitOfMeasurement',
      'article.pzn',
      'article.inventory.available',
      'article.status',
      'article.deletedAt',
    ])
    .nullish(),
  filters: z
    .object({
      status: Q.in(z.enum(ArticleStatusEnum.$.zodEnum())).nullish(),
      available: Q.in(z.enum(['yes', 'no'])).nullish(),
      noName: Q.in(z.enum(['empty'])).nullish(),
    })
    .optional(),
})

const filter = createQueryStore({
  schema: Filter,
})

const { label } = entityRegistry.article
function RouteComponent() {
  const [open, setOpen] = React.useState(false)
  // @Claude: single-letter variables are so 1980s :D
  const f = filter(useShallow(s => s))

  const articleParams = queryKit.parseQuery(f, { paginationMode: 'offset' })
  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('article')
        .selectAll()
        .select(sql`coalesce("article"."name", "article"."details" ->> '$.technicalName')`.as('name'))
        .$if(f.filters?.noName?.value?.includes('empty'), eb => eb.where('name', 'is', null))
        .$if(f.filters?.available?.value?.includes('yes'), eb =>
          eb.where(sql`inventory ->> '$.onStock')` as any, '>', 0),
        )
        .$if(f.filters?.available?.value?.includes('no'), eb =>
          eb.where(sql`inventory ->> '$.onStock')` as any, '=', 0),
        )
        .$if(f.filters?.status?.value, eb => eb.where('status', 'in', f.filters!.status!.value!))
        .$call(queryKit.search(articleParams))
        .$call(queryKit.sort(articleParams))
        .$call(queryKit.deleted(f.deleted))
        .$call(queryKit.paginate(articleParams)),
      params: articleParams,
    }),
  )

  const columns = useColumnDef<ResultTypeFromQueryResult<typeof data>>(
    () => [
      {
        key: 'article.name',
        title: label,
        fixed: 'start',
        render: (_, row) => (
          <Space vertical size="small">
            <Link to="/artikel/$id" params={{ id: row.id }} onClick={event => event.stopPropagation()}>
              <Space size="small">{row.name ?? `${row.sage?.source}:${row.details?.technicalName}`}</Space>
            </Link>
            <Space size="small" separator={<Divider vertical />}>
              <Typography.Text type="secondary">{row.manufacturer?.name}</Typography.Text>
              <Typography.Text type="secondary">{row.pzn?.replaceAll('"', '')}</Typography.Text>
            </Space>
          </Space>
        ),
      },
      {
        key: 'article.status',
        title: <Trans>Status</Trans>,
        render: (_, row) => <ArticleStatusBadge status={row.status} />,
      },
      {
        key: 'article.packaging.numberOfUnits',
        title: <Trans>Enthaltene Anzahl</Trans>,
        render: (_, row) => row.packaging?.numberOfUnits,
      },
      {
        key: 'article.packaging.amount',
        title: <Trans>Menge</Trans>,
        render: (_, row) => row.packaging?.amount,
      },
      {
        key: 'article.packaging.unitOfMeasurement',
        title: <Trans>Einheit</Trans>,
        render: (_, row) => row.packaging?.unitOfMeasurement,
      },
      {
        key: 'article.inventory.available',
        title: <Trans>Lieferbarkeit</Trans>,
        render: (_, row) => <BooleanStatusBadge status={row.inventory?.available as boolean} />,
      },
      {
        key: 'article.deletedAt',
        title: <Trans>Deaktiviert</Trans>,
        render: (_, row) => <BooleanStatusBadge status={!!row.deletedAt} />,
      },
      {
        key: 'actions',
        title: <Trans>Aktionen</Trans>,
        sorter: false,
        fixed: 'end',
        render: (_, row) => (
          <ButtonLink to="/artikel/$id" params={{ id: row.id }} size="small" shape="round">
            <Trans>Anzeigen</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    filter,
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space separator={<Divider vertical />} wrap={false}>
            <Space size="small">
              <SearchInput value={f.meta.searchRawValue} onChange={e => f.setSearch(e.target.value)} />
              <FilterButton activeFilterCount={f.meta.activeFilterCount} onClick={() => setOpen(true)} />
            </Space>
            <AddEntityButtonLink to="/artikel/$id" params={{ id: 'new' }} entityName={t`Wirkstoff`} />
          </Space>
        ),
        footer: <Divider size="small" style={{ marginBottom: 0 }} />,
      },
    },
    [f.meta.searchRawValue, f.setSearch, f.meta.activeFilterCount, setOpen, f.meta.activeFilterCount],
  )
  return (
    <QueryFilterWrapper
      queryStore={filter}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'status',
          label: <Trans>Status</Trans>,
          mode: 'multiple',
          options: ArticleStatusEnum.$.options.map(o => ({ value: o.value, label: o.label })),
        },
        {
          name: 'available',
          label: <Trans>Lagerbestand</Trans>,
          mode: 'multiple',
          options: [
            { value: 'yes', label: <Trans>Ja</Trans> },
            { value: 'no', label: <Trans>Nein</Trans> },
          ],
        },
        {
          name: 'noName',
          label: <Trans>Bezeichnung</Trans>,
          mode: 'multiple',
          options: [{ value: 'empty', label: 'Ohne Bezeichnung' }],
        },
      ]}
    >
      <Table
        className="min-w-full"
        dataSource={data?.data ?? []}
        loading={isFetching}
        columns={columns}
        rowKey="id"
        pagination={data}
        onChange={f.onChange}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </QueryFilterWrapper>
  )
}
